import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import IntroCard from 'components/IntroCard'
import { RowBetween, RowFixed } from 'components/Row'
import { LoadingRows } from 'pages/Pool/styleds'
import React, { useContext } from 'react'
import { Link, useHistory } from 'react-router-dom'
import styled, { ThemeContext } from 'styled-components'
import { HideSmall, TYPE } from 'theme'
import { CancelButton, InputText, InputTextWrapper, TableWrapper } from './styled'
import { useToken } from 'hooks/Tokens'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { Currency, WETH9 } from '@uniswap/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import { formatUnits } from 'ethers/lib/utils'
import { roundToDecimalPlace } from 'utils/data'
import { useActiveWeb3React } from 'hooks'
import { useWalletModalToggle } from 'state/application/hooks'
import { API_POOL_BASE_URL } from 'constants/links'
import { CURRENT_CHAIN } from '@hyperswap/addresses'
/* import Metrics from './Metrics' */
import { Helmet } from 'react-helmet'
import Tag from 'components/Tag'
import { formattedFeeAmount } from 'utils'
import axios from 'axios'

const PageWrapper = styled(AutoColumn)`
  max-width: 870px;
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    max-width: 800px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 500px;
  `};
`
const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
    flex-direction: column-reverse;
  `};
`
const ButtonRow = styled(RowFixed)`
  & > *:not(:last-child) {
    margin-right: 8px;
  }
  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
  `};
`
const NoLiquidity = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  max-width: 300px;
  min-height: 25vh;
`
const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  border-radius: 12px;
  padding: 6px 8px;
  width: fit-content;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex: 1 1 auto;
    width: 49%;
  `};
`

const MainContentWrapper = styled.main`
  background-color: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 8px;
  justify-content: center;
  button {
    width: 46px;
  }
  span {
    font-size: 14px;
    font-weight: 500;
  }
`

interface Reserves {
  reserve0: string
  reserve1: string
}

interface Token {
  tokenAddress: string
  tokenName: string
  tokenSymbol: string
  tokenDecimal: number
}

interface Pair {
  pairAddress: string
  token0: Token
  token1: Token
  reserves: Reserves
}

const TOTAL_PER_PAGE = 10

const PoolLogo: React.FC<{ tokenA: string; tokenB: string }> = ({ tokenA, tokenB }) => {
  const currencyA = useToken(tokenA)
  const currencyB = useToken(tokenB)

  return <DoubleCurrencyLogo size={20} currency0={currencyA as Currency} currency1={currencyB as Currency} />
}

const TokenLogo: React.FC<{ token: string }> = ({ token }) => {
  const currency = useToken(token)

  return <CurrencyLogo size={'20px'} currency={currency as Currency} />
}

const SmallButton = styled(ButtonPrimary)<{ active?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  background-color: ${({ theme, active }) => (active ? theme.primary1 : theme.bg3)}
  color: ${({ active }) => (active ? 'black' : 'white')}
  &:hover {
    color: black;
  }
  &:focus {
    color: black;
  }
  border-radius: 8px;
  padding: 4px 6px;
 
`

const ExplorePools = () => {
  const { account } = useActiveWeb3React()
  const history = useHistory()
  const toggleWalletModal = useWalletModalToggle()
  const [lpLoading, setLpLoading] = React.useState(false)
  const [data, setData] = React.useState<Pair[]>([])
  const theme = useContext(ThemeContext)
  const [currentPage, setCurrentPage] = React.useState(0)
  const [pageCount, setPageCount] = React.useState<number>(1)
  const [searchTerm, setSearchTerm] = React.useState('')

  const fetchData = async (page: number, tokenName = '') => {
    setLpLoading(true)
    try {
      const response = await axios.get(`${API_POOL_BASE_URL}/api/pairs`, {
        params: {
          page: page,
          maxPerPage: TOTAL_PER_PAGE,
          tokenName: tokenName,
        },
      })

      if (response.data.success) {
        setData(response.data.data.pairs)
        setPageCount(response.data.data.pageCount)
        const tableTopElement = document.getElementById('table-top')
        if (tableTopElement && response.data.data.pairs.length > 0) {
          tableTopElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } else {
        setData([])
        throw new Error('Failed to fetch data')
      }
    } catch (error) {
      console.error('Fetch Error:', error)
      setLpLoading(false)
    } finally {
      setLpLoading(false)
    }
  }

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData(currentPage, searchTerm)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchTerm, currentPage])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < pageCount) {
      setCurrentPage(newPage)
    }
  }

  return (
    <>
      <Helmet>
        <title>Explore Pools | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Discover all liquidity pools on HyperSwap Exchange. Built on Hyperliquid, our pools offer optimal earning opportunities for liquidity providers."
        />
        <meta
          name="keywords"
          content="HyperSwap pools, liquidity pools, Hyperliquid blockchain, DEX pools, DeFi, decentralized finance, Hyperliquid EVM"
        />
        <meta property="og:title" content="Explore Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Explore and invest in liquidity pools on HyperSwap Exchange, leveraging Hyperliquid's fast and secure EVM."
        />
        <meta property="og:url" content={`${window.location.origin}/#/explore/explore-pools`} />
      </Helmet>
      <PageWrapper>
        <AutoColumn gap="lg" justify="center">
          <div style={{ width: '100%' }}>
            <IntroCard variant={1} key="Explore-pools">
              <h1>Explore Pools</h1>
              <p>Discover all Liquidity Pools.</p>
            </IntroCard>
          </div>

          <AutoColumn gap="lg" style={{ width: '100%' }}>
            {/* <Metrics /> */}
            <TitleRow padding={'0'} id="table-top">
              <HideSmall>
                <TYPE.mediumHeader>Pools Overview</TYPE.mediumHeader>
              </HideSmall>
              <ButtonRow>
                <ResponsiveButtonPrimary id="join-pool-button" as={Link} to="/add/HYPE">
                  + New Position
                </ResponsiveButtonPrimary>
              </ButtonRow>
            </TitleRow>
            <InputTextWrapper>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="grey"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-search"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                <path d="M21 21l-6 -6" />
              </svg>
              <InputText
                type="text"
                placeholder="Search by token name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(0)
                }}
              />

              {searchTerm.length > 0 && (
                <CancelButton
                  onClick={() => {
                    setSearchTerm('')
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-x"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M18 6l-12 12" />
                    <path d="M6 6l12 12" />
                  </svg>
                </CancelButton>
              )}
            </InputTextWrapper>
            {account ? (
              <>
                {lpLoading ? (
                  <MainContentWrapper>
                    <LoadingRows>
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                    </LoadingRows>
                  </MainContentWrapper>
                ) : data.length > 0 ? (
                  <div style={{ maxWidth: '100%', overflow: 'auto' }}>
                    <TableWrapper>
                      <thead>
                        <tr>
                          <th colSpan={1} style={{ textAlign: 'center' }}>
                            #
                          </th>
                          <th>Pool</th>
                          <th>Reserve</th>
                          <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data.map((pool: any, index: number) => {
                          const display = pool.display
                          const addV2LPRoute = `/add${pool.version === 'v3' ? '/' : '/v2/'}${
                            pool.token0.token0Address === WETH9[CURRENT_CHAIN].address
                              ? 'HYPE'
                              : pool.token0.token0Address
                          }/${
                            pool.token1.token1Address === WETH9[CURRENT_CHAIN].address
                              ? 'HYPE'
                              : pool.token1.token1Address
                          }`

                          const swapRoute = `/swap${pool.version === 'v3' ? '/' : '/v2/'}?inputCurrency=${
                            pool.token0.token0Address === WETH9[CURRENT_CHAIN].address
                              ? 'HYPE'
                              : pool.token0.token0Address
                          }&outputCurrency=${
                            pool.token1.token1Address === WETH9[CURRENT_CHAIN].address
                              ? 'HYPE'
                              : pool.token1.token1Address
                          }`
                          if (display) {
                            return (
                              <tr key={pool.pairAddress}>
                                <td style={{ color: '#c3c5cb', fontSize: 14, fontWeight: 400, textAlign: 'center' }}>
                                  {index + 1 + TOTAL_PER_PAGE * currentPage}
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-start',
                                      gap: '12px',
                                    }}
                                  >
                                    <PoolLogo tokenA={pool.token0.token0Address} tokenB={pool.token1.token1Address} />
                                    <span style={{ fontWeight: 500, fontSize: 16 }}>
                                      {pool.token0.token0Symbol} / {pool.token1.token1Symbol}
                                    </span>

                                    <Tag variant={pool.version === 'v3' ? 2 : 1}>
                                      <div style={{ padding: '0 2px' }}>{pool.version === 'v3' ? 'v3' : 'v2'}</div>
                                    </Tag>

                                    <Tag variant={1}>
                                      <div style={{ padding: '0 2px' }}>
                                        {pool.fee ? formattedFeeAmount(pool.fee) : '0.3'}%
                                      </div>
                                    </Tag>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <TokenLogo token={pool.token0.token0Address} />
                                      <span style={{ fontSize: '14px' }}>
                                        {roundToDecimalPlace(
                                          `${formatUnits(pool.reserves.reserve0, pool.token0.token0Decimal)}`,
                                          '2'
                                        )}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <TokenLogo token={pool.token1.token1Address} />
                                      <span style={{ fontSize: '14px' }}>
                                        {roundToDecimalPlace(
                                          `${formatUnits(pool.reserves.reserve1, pool.token1.token1Decimal)}`,
                                          '2'
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' }}
                                >
                                  <SmallButton
                                    onClick={() => {
                                      history.push(addV2LPRoute)
                                      window.scrollTo({
                                        top: 0,
                                      })
                                    }}
                                  >
                                    Add Liquidity
                                  </SmallButton>
                                  <SmallButton
                                    onClick={() => {
                                      history.push(swapRoute)
                                      window.scrollTo({
                                        top: 0,
                                      })
                                    }}
                                  >
                                    Swap
                                  </SmallButton>
                                </td>
                              </tr>
                            )
                          } else {
                            return <></>
                          }
                        })}
                      </tbody>
                    </TableWrapper>

                    <PaginationContainer>
                      <ButtonSecondary onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevrons-left"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M11 7l-5 5l5 5" />
                          <path d="M17 7l-5 5l5 5" />
                        </svg>
                      </ButtonSecondary>

                      <ButtonSecondary onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M15 6l-6 6l6 6" />
                        </svg>
                      </ButtonSecondary>

                      <span>
                        Page {currentPage + 1}/{pageCount}{' '}
                      </span>

                      <ButtonSecondary
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage + 1 >= pageCount}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M9 6l6 6l-6 6" />
                        </svg>
                      </ButtonSecondary>
                      <ButtonSecondary
                        onClick={() => handlePageChange(pageCount - 1)}
                        disabled={currentPage + 1 >= pageCount}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-chevrons-right"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M7 7l5 5l-5 5" />
                          <path d="M13 7l5 5l-5 5" />
                        </svg>
                      </ButtonSecondary>
                    </PaginationContainer>
                  </div>
                ) : (
                  <MainContentWrapper>
                    <NoLiquidity>
                      <TYPE.mediumHeader color={theme.text3} textAlign="center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-photo-hexagon"
                          style={{ marginBottom: '.5rem' }}
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M15 8h.01" />
                          <path d="M19.875 6.27c.7 .398 1.13 1.143 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
                          <path d="M3.5 15.5l4.5 -4.5c.928 -.893 2.072 -.893 3 0l5 5" />
                          <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l2.5 2.5" />
                        </svg>
                        <div>Pools not found.</div>
                      </TYPE.mediumHeader>
                    </NoLiquidity>
                  </MainContentWrapper>
                )}
              </>
            ) : (
              <MainContentWrapper>
                <NoLiquidity>
                  <TYPE.mediumHeader color={theme.text3} textAlign="center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-photo-hexagon"
                      style={{ marginBottom: '.5rem' }}
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M15 8h.01" />
                      <path d="M19.875 6.27c.7 .398 1.13 1.143 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
                      <path d="M3.5 15.5l4.5 -4.5c.928 -.893 2.072 -.893 3 0l5 5" />
                      <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l2.5 2.5" />
                    </svg>
                    <div>Connect your wallet to explore pools.</div>
                  </TYPE.mediumHeader>
                  <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                    Connect a wallet
                  </ButtonPrimary>
                </NoLiquidity>
              </MainContentWrapper>
            )}
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
    </>
  )
}
export default ExplorePools
